import React, { useState } from 'react';
import { useModels } from '../contexts/ModelContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  BoltIcon,
  CpuChipIcon,
  XMarkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

function ModelRegistry() {
  const { models, updateModel, toggleArbiterSeal, autoRemediateModel, decommissionModel } = useModels();
  const { userProfile } = useAuth();
  const [sortBy, setSortBy] = useState('lastUpdated');
  const [filterBy, setFilterBy] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const handleAutoRemediate = async (modelId) => {
    const res = await autoRemediateModel(modelId);
    if (res.success) {
      setFeedback({ type: 'success', message: res.message });
      setTimeout(() => setFeedback(null), 5000);
      if (selectedModel && selectedModel.id === modelId) {
        setSelectedModel(null);
      }
    } else {
      setFeedback({ type: 'error', message: res.error });
      setTimeout(() => setFeedback(null), 5000);
    }
  };

  const handleDecommission = async (modelId) => {
    if (window.confirm('Confirm decommissioning this system under EU AI Act Article 5 to eliminate €35M statutory penalty exposure?')) {
      const res = await decommissionModel(modelId);
      if (res.success) {
        setFeedback({ type: 'success', message: 'System successfully decommissioned from active production.' });
        setTimeout(() => setFeedback(null), 5000);
        if (selectedModel && selectedModel.id === modelId) {
          setSelectedModel(null);
        }
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Passed':
        return <CheckCircleIcon className="h-4 w-4 text-emerald-600" />;
      case 'Failed':
        return <XCircleIcon className="h-4 w-4 text-red-600" />;
      case 'Pending':
        return <ClockIcon className="h-4 w-4 text-amber-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRiskTierBadge = (tier) => {
    switch (tier) {
      case 'Prohibited':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-700 text-white animate-pulse shadow-sm">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 mr-1" />
            BANNED (Art. 5)
          </span>
        );
      case 'High-Risk':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
            <ExclamationTriangleIcon className="h-3 w-3 mr-1 text-rose-600" />
            High-Risk
          </span>
        );
      case 'GPAI Systemic':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
            <CpuChipIcon className="h-3 w-3 mr-1 text-purple-600" />
            GPAI Systemic
          </span>
        );
      case 'Limited':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
            Limited (Art. 50)
          </span>
        );
      case 'Minimal':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200">
            Minimal Risk
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {tier}
          </span>
        );
    }
  };

  const handleDPOSignOff = async (modelId, currentStatus) => {
    if (userProfile?.role === 'DPO' || userProfile?.role === 'Executive') {
      await updateModel(modelId, { dpoSignOff: !currentStatus });
    }
  };

  const handleFRIAToggle = async (modelId, currentStatus) => {
    if (userProfile?.role === 'DPO' || userProfile?.role === 'Executive') {
      await updateModel(modelId, { friaCompleted: !currentStatus });
    }
  };

  const handleComplianceUpdate = async (modelId, newStatus) => {
    if (userProfile?.role === 'DPO' || userProfile?.role === 'Executive') {
      await updateModel(modelId, { complianceStatus: newStatus });
    }
  };

  const handleSealToggle = async (modelId) => {
    const res = await toggleArbiterSeal(modelId);
    if (!res.success && res.error) {
      alert(res.error);
    }
  };

  const filteredModels = models.filter(model => {
    // Search Term Filter
    if (searchTerm.trim() !== '') {
      const q = searchTerm.toLowerCase();
      const matchesSearch = 
        model.name?.toLowerCase().includes(q) ||
        model.baseModel?.toLowerCase().includes(q) ||
        model.useCase?.toLowerCase().includes(q) ||
        model.id?.toLowerCase().includes(q) ||
        model.jurisdiction?.toLowerCase().includes(q);
      if (!matchesSearch) return false;
    }

    if (filterBy === 'all') return true;
    if (filterBy === 'prohibited') return model.riskTier === 'Prohibited';
    if (filterBy === 'high-risk') return model.riskTier === 'High-Risk';
    if (filterBy === 'gpai') return model.riskTier === 'GPAI Systemic';
    if (filterBy === 'sealed') return model.jevArbiter?.sealed;
    if (filterBy === 'failed') return model.complianceStatus === 'Failed';
    if (filterBy === 'pending') return model.complianceStatus === 'Pending' || !model.dpoSignOff;
    return true;
  });

  const sortedModels = [...filteredModels].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'riskTier':
        const riskOrder = { 'Prohibited': 5, 'High-Risk': 4, 'GPAI Systemic': 3, 'Limited': 2, 'Minimal': 1 };
        return (riskOrder[b.riskTier] || 0) - (riskOrder[a.riskTier] || 0);
      case 'complianceStatus':
        return a.complianceStatus.localeCompare(b.complianceStatus);
      case 'latency':
        return (a.jevArbiter?.reflexLatencyMs || 99) - (b.jevArbiter?.reflexLatencyMs || 99);
      case 'lastUpdated':
      default:
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
    }
  });

  return (
    <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden card-lift">
      {/* Table Header and Filters */}
      <div className="px-6 py-5 border-b border-slate-200/80 bg-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-black text-slate-900 tracking-tight">
                Model Governance & Statutory Registry
              </h3>
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold border border-slate-200">
                {models.length} Registered
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Verified cryptographic hashes, Fundamental Rights Impact Assessments (FRIA Art. 27), and Jev Arbiter seals
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search Input */}
            <div className="relative min-w-[200px] flex-1 sm:flex-initial">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search models, base, use case..."
                className="w-full text-xs pl-9 pr-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/50 placeholder-slate-400 text-slate-800"
              />
            </div>

            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="text-xs border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-semibold text-slate-700 shadow-2xs"
            >
              <option value="all">Filter: All Systems</option>
              <option value="prohibited">🚨 Banned (EU Art. 5)</option>
              <option value="high-risk">Annex III High-Risk</option>
              <option value="gpai">GPAI Frontier Models</option>
              <option value="sealed">Jev Arbiter Sealed</option>
              <option value="pending">Pending Review / Sign-off</option>
              <option value="failed">Failed Compliance</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-semibold text-slate-700 shadow-2xs"
            >
              <option value="lastUpdated">Sort: Last Updated</option>
              <option value="name">Sort: Model Name</option>
              <option value="riskTier">Sort: Regulatory Tier</option>
              <option value="latency">Sort: Reflex Latency</option>
              <option value="complianceStatus">Sort: Status</option>
            </select>
          </div>
        </div>
      </div>

      {/* Feedback Banner */}
      {feedback && (
        <div className={`px-6 py-3 text-xs font-bold flex items-center justify-between transition-all ${
          feedback.type === 'success' 
            ? 'bg-emerald-50 text-emerald-800 border-b border-emerald-200' 
            : 'bg-rose-50 text-rose-800 border-b border-rose-200'
        }`}>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${feedback.type === 'success' ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
            <span>{feedback.message}</span>
          </div>
          <button onClick={() => setFeedback(null)} className="text-slate-400 hover:text-slate-700 font-bold ml-4">✕</button>
        </div>
      )}

      {/* Model Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="px-6 py-3.5 text-left">Model & Architecture</th>
              <th className="px-6 py-3.5 text-left">Use Case & Scope</th>
              <th className="px-6 py-3.5 text-left">Regulatory Tier</th>
              <th className="px-6 py-3.5 text-left">Jev System 1 Arbiter</th>
              <th className="px-6 py-3.5 text-left">FRIA / Colorado</th>
              <th className="px-6 py-3.5 text-left">Compliance</th>
              <th className="px-6 py-3.5 text-left">DPO Sign-Off</th>
              <th className="px-6 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 text-xs">
            {sortedModels.map((model) => (
              <tr 
                key={model.id} 
                className={`hover:bg-slate-50 transition-colors ${model.riskTier === 'Prohibited' ? 'bg-red-50/40' : ''}`}
              >
                {/* Model & Architecture */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900">{model.name}</span>
                      {model.riskTier === 'GPAI Systemic' && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-100 text-purple-800">
                          GPAI
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-[11px] mt-0.5">
                      {model.baseModel || 'Foundation Engine'} &bull; <span className="text-slate-600">{model.jurisdiction || 'Global'}</span>
                    </div>
                  </div>
                </td>

                {/* Use Case */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-800">{model.useCase}</div>
                  <div className="text-gray-400 text-[11px] truncate max-w-xs">{model.description}</div>
                </td>

                {/* Regulatory Tier */}
                <td className="px-6 py-4 whitespace-nowrap">
                  {getRiskTierBadge(model.riskTier)}
                </td>

                {/* Jev System 1 Arbiter */}
                <td className="px-6 py-4 whitespace-nowrap">
                  {model.jevArbiter?.sealed ? (
                    <div className="flex items-center gap-1.5">
                      <BoltIcon className="w-4 h-4 text-emerald-600" />
                      <div>
                        <span className="font-mono text-[10px] bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded border border-emerald-200">
                          {model.jevArbiter.sealHash.substring(0, 10)}...
                        </span>
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {model.jevArbiter.reflexLatencyMs} ms reflex &bull; Zero-PII
                        </div>
                      </div>
                    </div>
                  ) : model.riskTier === 'Prohibited' ? (
                    <span className="text-red-700 font-semibold text-[11px] flex items-center gap-1">
                      <XCircleIcon className="w-4 h-4 text-red-600" />
                      Arbiter Blocked
                    </span>
                  ) : (
                    <span className="text-gray-400 font-medium text-[11px] flex items-center gap-1">
                      <ClockIcon className="w-4 h-4 text-gray-400" />
                      Unsealed
                    </span>
                  )}
                </td>

                {/* Statutory Checks (FRIA & Colorado) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="text-gray-500 font-medium">FRIA:</span>
                      {model.friaCompleted ? (
                        <span className="text-emerald-700 font-semibold flex items-center">
                          <CheckCircleIcon className="w-3.5 h-3.5 mr-0.5" /> Done
                        </span>
                      ) : (
                        <span className="text-amber-600 font-medium flex items-center">
                          <ClockIcon className="w-3.5 h-3.5 mr-0.5" /> Pending
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="text-gray-500 font-medium">CO SB 205:</span>
                      {model.coloradoSB205Audited ? (
                        <span className="text-indigo-700 font-semibold">Audited</span>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </div>
                  </div>
                </td>

                {/* Compliance Status */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    {getStatusIcon(model.complianceStatus)}
                    <span className={`font-semibold ${
                      model.complianceStatus === 'Passed' ? 'text-emerald-700' :
                      model.complianceStatus === 'Failed' ? 'text-red-700' : 'text-amber-700'
                    }`}>
                      {model.complianceStatus}
                    </span>
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    EU Score: {model.frameworkScores?.euAiAct ?? 0}%
                  </div>
                </td>

                {/* DPO Sign-Off */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    {model.dpoSignOff ? (
                      <ShieldCheckIcon className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <ShieldExclamationIcon className="h-5 w-5 text-gray-400" />
                    )}
                    <span className={`text-[11px] font-medium ${model.dpoSignOff ? 'text-emerald-800' : 'text-gray-500'}`}>
                      {model.dpoSignOff ? 'Signed' : 'Awaiting'}
                    </span>
                  </div>
                </td>

                {/* Actions */}
                <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-medium space-x-1.5">
                  <button
                    onClick={() => setSelectedModel(model)}
                    className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-colors"
                  >
                    Inspect
                  </button>

                  {/* EU AI Act Article 5 Safe Decommission Trigger */}
                  {model.riskTier === 'Prohibited' && model.complianceStatus !== 'Decommissioned' && (
                    <button
                      onClick={() => handleDecommission(model.id)}
                      className="inline-flex items-center px-2.5 py-1 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-xs transition-colors"
                      title="Decommission prohibited system to eliminate €35M statutory penalty"
                    >
                      Decommission
                    </button>
                  )}

                  {/* Closed-Loop Jev Reflex Auto-Remediation Trigger */}
                  {model.riskTier !== 'Prohibited' && (model.complianceStatus !== 'Passed' || !model.jevArbiter?.sealed) && (
                    <button
                      onClick={() => handleAutoRemediate(model.id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold shadow-xs transition-all transform hover:-translate-y-0.5"
                      title="Auto-remediate: Provision Jev System 1 Reflex Guardrail & Seal"
                    >
                      <BoltIcon className="w-3.5 h-3.5 text-white" />
                      Auto-Remediate
                    </button>
                  )}

                  {(userProfile?.role === 'DPO' || userProfile?.role === 'Executive') && (
                    <>
                      {/* DPO Sign-off button */}
                      <button
                        onClick={() => handleDPOSignOff(model.id, model.dpoSignOff)}
                        className={`inline-flex items-center px-2 py-1 rounded font-medium transition-colors ${
                          model.dpoSignOff
                            ? 'text-red-700 bg-red-50 hover:bg-red-100'
                            : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                        }`}
                        title={model.dpoSignOff ? 'Revoke DPO Approval' : 'Approve DPO Sign-off'}
                      >
                        {model.dpoSignOff ? 'Revoke DPO' : 'DPO Sign'}
                      </button>

                      {/* FRIA toggle for High-Risk */}
                      {model.riskTier === 'High-Risk' && (
                        <button
                          onClick={() => handleFRIAToggle(model.id, model.friaCompleted)}
                          className={`inline-flex items-center px-2 py-1 rounded font-medium transition-colors ${
                            model.friaCompleted
                              ? 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100'
                              : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                          }`}
                          title="Toggle Fundamental Rights Impact Assessment"
                        >
                          {model.friaCompleted ? 'FRIA ✓' : 'Add FRIA'}
                        </button>
                      )}

                      {/* Compliance Status Fast Pass/Fail */}
                      {model.complianceStatus === 'Pending' && (
                        <>
                          <button
                            onClick={() => handleComplianceUpdate(model.id, 'Passed')}
                            className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs"
                          >
                            Pass
                          </button>
                          <button
                            onClick={() => handleComplianceUpdate(model.id, 'Failed')}
                            className="px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-medium shadow-xs"
                          >
                            Fail
                          </button>
                        </>
                      )}
                    </>
                  )}

                  {/* Jev Arbiter Seal Toggle (Available to all roles for authorized testing) */}
                  {model.riskTier !== 'Prohibited' && (
                    <button
                      onClick={() => handleSealToggle(model.id)}
                      className={`inline-flex items-center px-2 py-1 rounded font-medium transition-colors ${
                        model.jevArbiter?.sealed
                          ? 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                          : 'text-emerald-800 bg-emerald-100 hover:bg-emerald-200'
                      }`}
                      title="Toggle Jev System 1 Arbiter Cryptographic Seal"
                    >
                      <BoltIcon className="w-3.5 h-3.5 mr-0.5" />
                      {model.jevArbiter?.sealed ? 'Unseal' : 'Seal'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedModels.length === 0 && (
        <div className="text-center py-12 bg-white">
          <div className="text-gray-400 text-sm">No models found matching the current regulatory filter.</div>
        </div>
      )}

      {/* Model Deep Inspection Modal */}
      {selectedModel && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 relative border border-gray-200 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start border-b border-gray-100 pb-4 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-gray-900">{selectedModel.name}</h3>
                  {getRiskTierBadge(selectedModel.riskTier)}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ID: <span className="font-mono text-gray-700">{selectedModel.id}</span> &bull; Last Audit: {selectedModel.lastUpdated}
                </p>
              </div>
              <button 
                onClick={() => setSelectedModel(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {/* Statutory Classification */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="font-bold text-slate-800 text-sm mb-1">
                  Statutory Classification & Penalties
                </div>
                <div className="text-gray-600 mb-2">
                  {selectedModel.regulation || 'EU AI Act & Colorado SB 24-205 Assessment'}
                </div>
                <div className="text-gray-500">
                  {selectedModel.description}
                </div>
              </div>

              {/* Jev System 1 Cryptographic Reflex Arbiter Details */}
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-emerald-900 text-sm flex items-center gap-1.5">
                    <BoltIcon className="w-4 h-4 text-emerald-700" />
                    Jev System 1 Reflex Arbiter Telemetry
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${selectedModel.jevArbiter?.sealed ? 'bg-emerald-200 text-emerald-900' : 'bg-red-200 text-red-900'}`}>
                    {selectedModel.jevArbiter?.sealed ? 'CRYPTOGRAPHICALLY SEALED' : 'UNSEALED'}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 text-emerald-950">
                  <div>
                    <div className="text-gray-500 text-[10px]">Arbiter Hash</div>
                    <div className="font-mono font-bold text-[11px] truncate">
                      {selectedModel.jevArbiter?.sealHash || 'None'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-[10px]">Edge Reflex Latency</div>
                    <div className="font-bold">{selectedModel.jevArbiter?.reflexLatencyMs || 0} ms</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-[10px]">OWASP LLM01 Injection</div>
                    <div className="font-bold">{selectedModel.jevArbiter?.injectionShield || 'Active'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-[10px]">PII Sanitization</div>
                    <div className="font-bold">{selectedModel.jevArbiter?.piiSanitization || 'Zero-Knowledge Tokenized'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-[10px]">Last Attestation</div>
                    <div className="font-mono text-[10px]">{selectedModel.jevArbiter?.lastAttestation?.split('T')[0] || selectedModel.lastUpdated}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-[10px]">HITL Oversight (Art. 14)</div>
                    <div className="font-bold">Human-in-the-Loop</div>
                  </div>
                </div>
              </div>

              {/* Multi-Framework Scores */}
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="font-bold text-gray-800 text-sm mb-3">Framework Compliance Breakdown</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <div className="text-gray-500 text-[10px]">EU AI Act</div>
                    <div className="text-base font-bold text-blue-700">{selectedModel.frameworkScores?.euAiAct ?? 80}%</div>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <div className="text-gray-500 text-[10px]">Colorado SB 205</div>
                    <div className="text-base font-bold text-indigo-700">{selectedModel.frameworkScores?.coloradoSb205 ?? 80}%</div>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <div className="text-gray-500 text-[10px]">NIST AI RMF</div>
                    <div className="text-base font-bold text-teal-700">{selectedModel.frameworkScores?.nistAiRmf ?? 85}%</div>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                    <div className="text-gray-500 text-[10px]">ISO/IEC 42001</div>
                    <div className="text-base font-bold text-purple-700">{selectedModel.frameworkScores?.iso42001 ?? 85}%</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <div className="flex items-center gap-2">
                {selectedModel.riskTier === 'Prohibited' && selectedModel.complianceStatus !== 'Decommissioned' && (
                  <button
                    onClick={() => handleDecommission(selectedModel.id)}
                    className="px-3.5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shadow-xs transition-colors"
                  >
                    🛑 Decommission Prohibited Practice (€35M Fine Shield)
                  </button>
                )}

                {selectedModel.riskTier !== 'Prohibited' && (selectedModel.complianceStatus !== 'Passed' || !selectedModel.jevArbiter?.sealed) && (
                  <button
                    onClick={() => handleAutoRemediate(selectedModel.id)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold shadow-xs transition-all"
                  >
                    <BoltIcon className="w-3.5 h-3.5" />
                    Auto-Remediate with Jev System 1 Reflex
                  </button>
                )}
              </div>

              <button
                onClick={() => setSelectedModel(null)}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModelRegistry;